import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as THREE from 'three';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader';
import FeatureTree from './FeatureTree';
import ReplicadViewer from './ReplicadViewer';
import SketchViewer from './SketchViewer';
import { setupScene, animateScene, centerAndFitModel } from '../utils/sceneSetup';
import { getSketchInfo } from '../services/api';
import './ModelViewer.css';

const BODY_COLORS = [0x0071e3, 0x34c759, 0xff9500, 0xaf52de, 0x5ac8fa, 0xff3b30];

async function downloadFile(url, defaultFilename) {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
  const password = sessionStorage.getItem('access_password');
  const headers = password ? { 'X-Access-Password': password } : {};
  const response = await fetch(`${backendUrl}${url}`, { headers });
  if (!response.ok) throw new Error(`Download failed (${response.status})`);
  const blob = await response.blob();
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = defaultFilename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(blobUrl);
}

function ModelViewer({ modelUrl, stepUrl, parts, featureTree, bodyMeshes, selectedBody, selectedFeature, onFeatureSelect }) {
  const mountRef = useRef(null);
  const sceneRef = useRef(null);
  const controlsRef = useRef(null);
  const meshRef = useRef(null);
  const meshesRef = useRef({});
  const loadGenerationRef = useRef(0);
  const [wireframe, setWireframe] = useState(false);
  const [flatShading, setFlatShading] = useState(false);
  const [viewMode, setViewMode] = useState('mesh');
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [sketchData, setSketchData] = useState(null);
  const [sketchName, setSketchName] = useState(null);
  const downloadRef = useRef(null);

  // Setup scene
  useEffect(() => {
    if (!mountRef.current) return;

    const { scene, camera, renderer, controls } = setupScene(mountRef.current);
    sceneRef.current = scene;
    controlsRef.current = controls;

    animateScene(renderer, scene, camera, controls);

    const handleResize = () => {
      if (!mountRef.current) return;
      camera.aspect = mountRef.current.clientWidth / mountRef.current.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(mountRef.current.clientWidth, mountRef.current.clientHeight);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      const mount = mountRef.current;
      if (mount && renderer.domElement) mount.removeChild(renderer.domElement);
      renderer.dispose();
    };
  }, []);

  // Load per-body STLs when available
  useEffect(() => {
    if (!bodyMeshes?.length || !sceneRef.current) return;

    const generation = ++loadGenerationRef.current;

    // Clear old per-body meshes
    Object.values(meshesRef.current).forEach(m => {
      sceneRef.current.remove(m);
      m.geometry.dispose();
      m.material.dispose();
    });
    meshesRef.current = {};

    // Clear single mesh if present
    if (meshRef.current) {
      sceneRef.current.remove(meshRef.current);
      meshRef.current.geometry.dispose();
      meshRef.current.material.dispose();
      meshRef.current = null;
    }

    const loader = new STLLoader();
    const password = sessionStorage.getItem('access_password');
    if (password) {
      loader.setRequestHeader({ 'X-Access-Password': password });
    }
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
    let loadedCount = 0;

    bodyMeshes.forEach((body, idx) => {
      loader.load(
        `${backendUrl}${body.stl_path}`,
        (geometry) => {
          // Stale load: a newer update has already started
          if (generation !== loadGenerationRef.current) {
            geometry.dispose();
            return;
          }

          geometry.computeVertexNormals();

          const material = new THREE.MeshPhysicalMaterial({
            color: BODY_COLORS[idx % BODY_COLORS.length],
            metalness: 0.1,
            roughness: 0.4,
            clearcoat: 0.3,
            clearcoatRoughness: 0.25,
            side: THREE.DoubleSide,
            wireframe,
            flatShading,
            transparent: false,
            opacity: 1.0,
          });

          const mesh = new THREE.Mesh(geometry, material);
          sceneRef.current.add(mesh);
          meshesRef.current[body.name] = mesh;

          loadedCount++;
          // Center camera after all bodies loaded
          if (loadedCount === bodyMeshes.length) {
            const group = new THREE.Group();
            Object.values(meshesRef.current).forEach(m => group.add(m.clone()));
            centerAndFitModel(group, controlsRef.current);
            // Clean up temp group
            group.children.forEach(c => { c.geometry.dispose(); c.material.dispose(); });
          }
        },
        null,
        (error) => {
          if (generation !== loadGenerationRef.current) return;
          if (error.message?.includes('401')) {
            sessionStorage.removeItem('access_password');
            window.location.reload();
            return;
          }
          console.error(`Error loading STL for ${body.name}:`, error);
        }
      );
    });

    if (onFeatureSelect) onFeatureSelect(null, null);
  }, [bodyMeshes, wireframe, flatShading, onFeatureSelect]);

  // Fallback: load single merged STL when no per-body meshes
  useEffect(() => {
    if (bodyMeshes?.length || !modelUrl || !sceneRef.current) return;

    if (meshRef.current) {
      sceneRef.current.remove(meshRef.current);
      meshRef.current.geometry.dispose();
      meshRef.current.material.dispose();
    }

    const loader = new STLLoader();
    const password = sessionStorage.getItem('access_password');
    if (password) {
      loader.setRequestHeader({ 'X-Access-Password': password });
    }
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

    loader.load(
      `${backendUrl}${modelUrl}`,
      (geometry) => {
        geometry.computeVertexNormals();

        const material = new THREE.MeshPhysicalMaterial({
          color: 0x0071e3,
          metalness: 0.1,
          roughness: 0.4,
          clearcoat: 0.3,
          clearcoatRoughness: 0.25,
          side: THREE.DoubleSide,
          wireframe,
          flatShading
        });

        const mesh = new THREE.Mesh(geometry, material);
        sceneRef.current.add(mesh);
        meshRef.current = mesh;

        centerAndFitModel(mesh, controlsRef.current);
      },
      null,
      (error) => {
        if (error.message?.includes('401')) {
          sessionStorage.removeItem('access_password');
          window.location.reload();
          return;
        }
        console.error('Error loading STL:', error);
      }
    );
  }, [modelUrl, bodyMeshes, wireframe, flatShading]);

  // Update material properties
  useEffect(() => {
    if (meshRef.current?.material) {
      meshRef.current.material.wireframe = wireframe;
      meshRef.current.material.needsUpdate = true;
    }
    Object.values(meshesRef.current).forEach(m => {
      m.material.wireframe = wireframe;
      m.material.needsUpdate = true;
    });
  }, [wireframe]);

  useEffect(() => {
    if (meshRef.current?.material) {
      meshRef.current.material.flatShading = flatShading;
      meshRef.current.material.needsUpdate = true;
    }
    Object.values(meshesRef.current).forEach(m => {
      m.material.flatShading = flatShading;
      m.material.needsUpdate = true;
    });
  }, [flatShading]);

  // Close download menu on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (downloadRef.current && !downloadRef.current.contains(e.target)) {
        setShowDownloadMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch sketch geometry when a SketchObject is selected
  useEffect(() => {
    if (!selectedFeature || !featureTree) {
      setSketchData(null);
      setSketchName(null);
      return;
    }
    // Find the feature in the tree to check its type
    let featureType = null;
    for (const body of featureTree) {
      for (const feat of body.features) {
        if (feat.name === selectedFeature) {
          featureType = feat.type;
          break;
        }
      }
      if (featureType) break;
    }
    if (featureType !== 'SketchObject') {
      setSketchData(null);
      setSketchName(null);
      return;
    }
    // Fetch sketch info
    getSketchInfo(selectedFeature)
      .then(data => {
        setSketchData(data.geometry || []);
        setSketchName(selectedFeature);
      })
      .catch(() => {
        setSketchData(null);
        setSketchName(null);
      });
  }, [selectedFeature, featureTree]);

  // Highlight handler for feature tree clicks
  const handleFeatureClick = useCallback((bodyName, featureName) => {
    if (onFeatureSelect) onFeatureSelect(bodyName, featureName);
  }, [onFeatureSelect]);

  // Update mesh opacity based on selection
  useEffect(() => {
    if (!selectedBody) {
      Object.values(meshesRef.current).forEach(mesh => {
        mesh.material.opacity = 1.0;
        mesh.material.transparent = false;
        mesh.material.needsUpdate = true;
      });
    } else {
      Object.entries(meshesRef.current).forEach(([name, mesh]) => {
        if (name === selectedBody) {
          mesh.material.opacity = 1.0;
          mesh.material.transparent = false;
        } else {
          mesh.material.opacity = 0.3;
          mesh.material.transparent = true;
        }
        mesh.material.needsUpdate = true;
      });
    }
  }, [selectedBody]);

  return (
    <div className="model-viewer">
      <div className="viewer-header">
        <h3>3D Preview</h3>

        <div className="viewer-header-right">
          <div className="view-mode-selector">
            <button
              className={viewMode === 'mesh' ? 'active' : ''}
              onClick={() => setViewMode('mesh')}
              title="Fast mesh preview (STL)"
            >
              Mesh
            </button>
            <button
              className={viewMode === 'cad' ? 'active' : ''}
              onClick={() => setViewMode('cad')}
              disabled={!stepUrl}
              title="Exact CAD surfaces (STEP)"
            >
              CAD
            </button>
          </div>

          {(modelUrl || stepUrl) && (
            <div className="download-dropdown" ref={downloadRef}>
              <button
                className="download-trigger"
                onClick={() => setShowDownloadMenu(prev => !prev)}
              >
                Download ▾
              </button>
              {showDownloadMenu && (
                <div className="download-menu">
                  {modelUrl && (
                    <button onClick={() => { downloadFile(modelUrl, 'model.stl'); setShowDownloadMenu(false); }}>
                      Download STL
                    </button>
                  )}
                  {stepUrl && (
                    <button onClick={() => { downloadFile(stepUrl, 'model.step'); setShowDownloadMenu(false); }}>
                      Download STEP
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Viewer area with overlay */}
      <div className="viewer-area">
        <FeatureTree featureTree={featureTree} onFeatureClick={handleFeatureClick} selectedBody={selectedBody} selectedFeature={selectedFeature} />

        {/* Mesh View */}
        <div
          ref={mountRef}
          className="viewer-canvas"
          style={{ display: viewMode === 'mesh' ? 'block' : 'none' }}
        />

        {/* Sketch Viewer Overlay */}
        {sketchData && sketchName && (
          <SketchViewer
            sketchName={sketchName}
            geometry={sketchData}
            onClose={() => { setSketchData(null); setSketchName(null); }}
          />
        )}
      </div>

      {/* CAD View */}
      {viewMode === 'cad' && (
        <div className="viewer-canvas">
          {stepUrl ? (
            <ReplicadViewer stepUrl={stepUrl} />
          ) : (
            <div className="empty-viewer">
              <p>No CAD model available</p>
              <p className="hint">Create a model first</p>
            </div>
          )}
        </div>
      )}

      {viewMode === 'mesh' && !modelUrl && (!bodyMeshes || bodyMeshes.length === 0) && (
        <div className="empty-viewer">
          <p>Your 3D model will appear here</p>
          <p className="hint">Start by describing what you want to create in the chat</p>
        </div>
      )}
    </div>
  );
}

export default ModelViewer;
